// FILE: src/pages/Caregivers/CaregiversPage.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { caregiversAPI } from '../../api/services';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import '../../components/UI/UI.css';
import './Caregivers.css';

const initials = (first, last) => `${first?.[0]||''}${last?.[0]||''}`.toUpperCase();
const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-US',{year:'numeric',month:'short',day:'numeric'}) : '—';
const colors   = ['#1a4d8c','#059669','#d97706','#7c3aed','#db2777','#0891b2'];

const EMPTY_FORM = {
  FirstName:'', LastName:'', Email:'', Username:'', Password:'',
  PhoneNumber:'', CellNumber:'', DateOfBirth:'', Gender:'',
  Address:'', City:'', State:'', ZipCode:'',
  HireDate:'', HourlyRate:'', PayType:'Hourly',
  SSN:'', BackgroundCheckDate:'', DriversLicense:'',
  MaxHoursPerWeek:'40', Specializations:'',
};

// ── Field component — defined OUTSIDE to prevent focus-loss on re-render ──
function Field({ label, field, type, required, options, span, form, errors, onChange }) {
  const hasError = errors && errors[field];
  return (
    <div className="form-group" style={span ? { gridColumn: `span ${span}` } : {}}>
      <label className="form-label">
        {label}{required && <span className="req"> *</span>}
      </label>
      {options ? (
        <select
          className="form-control"
          value={form[field] ?? ''}
          onChange={e => onChange(field, e.target.value)}
        >
          <option value="">— Select —</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          className={`form-control${hasError ? ' input-error' : ''}`}
          type={type || 'text'}
          value={form[field] ?? ''}
          onChange={e => onChange(field, e.target.value)}
          placeholder={label}
        />
      )}
      {hasError && (
        <div style={{ color: 'var(--danger)', fontSize: '0.75rem', marginTop: 3 }}>
          {errors[field]}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────
export default function CaregiversPage() {
  const { hasRole } = useAuth();
  const canEdit     = hasRole('Admin', 'CaseManager');

  const [caregivers, setCaregivers] = useState([]);
  const [filtered,   setFiltered]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [showModal,  setShowModal]  = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [deleting,   setDeleting]   = useState(null);
  const [delLoading, setDelLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await caregiversAPI.getAll();
      setCaregivers(res.data.data || []);
      setFiltered(res.data.data || []);
    } catch { toast.error('Failed to load caregivers.'); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!search.trim()) { setFiltered(caregivers); return; }
    const q = search.toLowerCase();
    setFiltered(caregivers.filter(c =>
      `${c.FirstName} ${c.LastName}`.toLowerCase().includes(q) ||
      (c.Email||'').toLowerCase().includes(q) ||
      (c.PhoneNumber||'').includes(q) ||
      (c.Specializations||'').toLowerCase().includes(q)
    ));
  }, [search, caregivers]);

  const handleToggle = async (c) => {
    try {
      await caregiversAPI.toggleStatus(c.CaregiverID);
      toast.success(`${c.FirstName} ${c.LastName} status updated.`);
      load();
    } catch { toast.error('Failed to update status.'); }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDelLoading(true);
    try {
      await caregiversAPI.delete(deleting.CaregiverID);
      toast.success(`${deleting.FirstName} ${deleting.LastName} deleted.`);
      setDeleting(null);
      load();
    } catch { toast.error('Delete failed.'); }
    finally   { setDelLoading(false); }
  };

  const active   = caregivers.filter(c => c.IsActive);
  const inactive = caregivers.filter(c => !c.IsActive);

  return (
    <div>
      <div className="page-header">
        <div className="page-header-left">
          <h1>Caregivers</h1>
          <p>{caregivers.length} total · {active.length} active</p>
        </div>
        <div style={{display:'flex',gap:10}}>
          <div className="search-bar">
            <span className="search-icon">🔍</span>
            <input className="form-control" placeholder="Search name, email, specialization…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{paddingLeft:36,minWidth:260}} />
          </div>
          {canEdit && (
            <button className="btn btn-primary" onClick={() => { setEditing(null); setShowModal(true); }}>
              ➕ Add Caregiver
            </button>
          )}
        </div>
      </div>

      <div className="stats-grid-4" style={{marginBottom:20}}>
        {[
          {label:'Total Caregivers', value:caregivers.length, icon:'👨‍⚕️'},
          {label:'Active',           value:active.length,      icon:'✅'},
          {label:'Inactive',         value:inactive.length,    icon:'⏸️'},
          {label:'Avg Max Hrs/Wk',   icon:'⏰',
           value: caregivers.length > 0
             ? Math.round(caregivers.reduce((a,c)=>a+(parseFloat(c.MaxHoursPerWeek)||0),0)/caregivers.length)+'h'
             : '—'},
        ].map(s => (
          <div className="stat-card" key={s.label}>
            <div className="stat-card-header">
              <span className="stat-card-label">{s.label}</span>
              <span style={{fontSize:20}}>{s.icon}</span>
            </div>
            <div className="stat-card-value">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="card" style={{padding:0}}>
        {loading ? (
          <div className="loading-state">⏳ Loading caregivers…</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👨‍⚕️</div>
            <div className="empty-state-text">{search ? 'No caregivers match.' : 'No caregivers yet.'}</div>
            {!search && canEdit && (
              <button className="btn btn-primary" style={{marginTop:14}}
                onClick={() => { setEditing(null); setShowModal(true); }}>
                ➕ Add First Caregiver
              </button>
            )}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="ec-table">
              <thead>
                <tr>
                  <th>Caregiver</th><th>Contact</th><th>Hire Date</th>
                  <th>Pay Rate</th><th>Max Hrs</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.CaregiverID}>
                    <td>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <div className="caregiver-avatar"
                          style={{background: c.IsActive ? colors[c.CaregiverID % colors.length] : 'var(--gray-300)'}}>
                          {initials(c.FirstName, c.LastName)}
                        </div>
                        <div>
                          <div style={{fontWeight:600,color:'var(--gray-800)'}}>
                            {c.FirstName} {c.LastName}
                          </div>
                          <div style={{fontSize:'0.75rem',color:'var(--gray-400)'}}>
                            {c.Specializations || 'General Care'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{fontSize:'0.82rem'}}>{c.CellNumber||c.PhoneNumber||'—'}</div>
                      <div style={{fontSize:'0.75rem',color:'var(--gray-400)'}}>{c.Email||''}</div>
                    </td>
                    <td style={{fontSize:'0.82rem',color:'var(--gray-600)'}}>{fmtDate(c.HireDate)}</td>
                    <td style={{fontSize:'0.82rem'}}>{c.HourlyRate ? `$${parseFloat(c.HourlyRate).toFixed(2)}/hr` : '—'}</td>
                    <td style={{fontSize:'0.82rem'}}>{c.MaxHoursPerWeek ? `${c.MaxHoursPerWeek}h/wk` : '—'}</td>
                    <td>
                      <span className={`badge ${c.IsActive ? 'badge-success' : 'badge-gray'}`}>
                        {c.IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{display:'flex',gap:6}}>
                        {canEdit && (
                          <>
                            <button className="btn btn-sm btn-secondary"
                              onClick={() => { setEditing(c); setShowModal(true); }}>✏️</button>
                            <button
                              className={`btn btn-sm ${c.IsActive ? 'btn-secondary' : 'btn-success'}`}
                              onClick={() => handleToggle(c)}>
                              {c.IsActive ? '⏸️' : '▶️'}
                            </button>
                            <button className="btn btn-sm btn-danger"
                              onClick={() => setDeleting(c)}>🗑️</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <CaregiverFormModal
          caregiver={editing}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
        />
      )}

      {deleting && (
        <div className="modal-overlay">
          <div className="modal modal-sm">
            <div className="modal-header">
              <span className="modal-title">🗑️ Delete Caregiver</span>
              <button className="modal-close" onClick={() => setDeleting(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{color:'var(--gray-600)'}}>
                Delete <strong>{deleting.FirstName} {deleting.LastName}</strong>?
                This will also delete their user account.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setDeleting(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={delLoading}>
                {delLoading ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Caregiver Form Modal ──────────────────────────────────
const TABS_CG = ['Account', 'Personal', 'Employment'];

function CaregiverFormModal({ caregiver, onClose, onSaved }) {
  const isEdit = !!caregiver;
  const [tab,    setTab]    = useState(0);
  const [form,   setForm]   = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (caregiver) {
      const f = {};
      Object.keys(EMPTY_FORM).forEach(k => {
        if (['DateOfBirth','HireDate','BackgroundCheckDate'].includes(k)) {
          f[k] = caregiver[k] ? caregiver[k].split('T')[0] : '';
        } else {
          f[k] = caregiver[k] ?? '';
        }
      });
      f.Password = '';
      setForm(f);
    }
  }, [caregiver]);

  // Stable onChange — does NOT recreate Field on re-render
  const handleChange = (field, val) => {
    setForm(p => ({ ...p, [field]: val }));
    if (errors[field]) setErrors(p => ({ ...p, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.FirstName.trim())  e.FirstName = 'Required';
    if (!form.LastName.trim())   e.LastName  = 'Required';
    if (!isEdit) {
      if (!form.Email.trim())    e.Email    = 'Required';
      if (!form.Username.trim()) e.Username = 'Required';
      if (!form.Password.trim()) e.Password = 'Required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) { setTab(0); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await caregiversAPI.update(caregiver.CaregiverID, form);
        toast.success('Caregiver updated.');
      } else {
        await caregiversAPI.create(form);
        toast.success('Caregiver added. Login credentials created.');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed.');
    } finally { setSaving(false); }
  };

  // Shared props — same object reference for all Field instances
  const fp = { form, errors, onChange: handleChange };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <span className="modal-title">
            {isEdit ? `✏️ Edit Caregiver — ${caregiver.FirstName} ${caregiver.LastName}` : '➕ Add New Caregiver'}
          </span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-tabs">
          {TABS_CG.map((t, i) => (
            <button key={t} className={`modal-tab${tab === i ? ' active' : ''}`} onClick={() => setTab(i)}>
              {t}
            </button>
          ))}
        </div>

        <div className="modal-body">
          {/* Tab 0 — Account */}
          {tab === 0 && (
            <div className="form-row-3">
              <Field label="First Name" field="FirstName" required {...fp} />
              <Field label="Last Name"  field="LastName"  required {...fp} />
              <Field label="Email"      field="Email"     type="email" required={!isEdit} {...fp} />
              <Field label="Username"   field="Username"  required={!isEdit} {...fp} />
              <Field
                label={isEdit ? 'New Password (leave blank to keep)' : 'Password'}
                field="Password" type="password" required={!isEdit} {...fp}
              />
            </div>
          )}

          {/* Tab 1 — Personal */}
          {tab === 1 && (
            <div className="form-row-3">
              <Field label="Phone"         field="PhoneNumber" type="tel"  {...fp} />
              <Field label="Cell"          field="CellNumber"  type="tel"  {...fp} />
              <Field label="Date of Birth" field="DateOfBirth" type="date" {...fp} />
              <Field label="Gender" field="Gender"
                options={['Male','Female','Non-binary','Prefer not to say']} {...fp} />
              <Field label="Address" field="Address" span={2} {...fp} />
              <Field label="City"    field="City"  {...fp} />
              <Field label="State"   field="State"
                options={['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
                  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM',
                  'NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY']}
                {...fp} />
              <Field label="Zip Code"              field="ZipCode"             {...fp} />
              <Field label="Driver's License"      field="DriversLicense"      {...fp} />
              <Field label="Background Check Date" field="BackgroundCheckDate" type="date" {...fp} />
            </div>
          )}

          {/* Tab 2 — Employment */}
          {tab === 2 && (
            <div className="form-row-3">
              <Field label="Hire Date"       field="HireDate"        type="date"   {...fp} />
              <Field label="Hourly Rate ($)" field="HourlyRate"      type="number" {...fp} />
              <Field label="Pay Type"        field="PayType"
                options={['Hourly','Salary']} {...fp} />
              <Field label="Max Hrs/Week"    field="MaxHoursPerWeek" type="number" {...fp} />
              <Field label="Specializations" field="Specializations" span={2}      {...fp} />
              <Field label="SSN (last 4)"    field="SSN"                           {...fp} />
            </div>
          )}
        </div>

        <div className="modal-footer" style={{justifyContent:'space-between'}}>
          <div style={{display:'flex',gap:8}}>
            {tab > 0 && (
              <button className="btn btn-secondary" onClick={() => setTab(t => t - 1)}>← Back</button>
            )}
            {tab < TABS_CG.length - 1 && (
              <button className="btn btn-secondary" onClick={() => setTab(t => t + 1)}>Next →</button>
            )}
          </div>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : isEdit ? '💾 Update' : '✅ Add Caregiver'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}